// ---------------------------------------------------------------------------
// SaveSyntheticDatasetDialog — Modal that captures the dataset name before
// the synthetic preview gets persisted to the Data Hub. Other metadata
// (access category, sensitivity label, data type) defaults to Public /
// general / Generic for the v1 synthetic flow; users can adjust these later
// on the dataset detail page.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
} from '@fluentui/react-components';
import { useCallback, useEffect, useState } from 'react';

import type {
  SaveSyntheticDatasetDialogProps,
  SaveSyntheticDatasetFormValues,
} from './SaveSyntheticDatasetDialog.interface.js';
import { useStyles } from './SaveSyntheticDatasetDialog.style.js';

const NAME_REQUIRED_ERROR = 'Dataset name is required.';

const DEFAULTS = {
  accessCategory: 'Public',
  sensitivityLabel: 'general',
  dataType: 'Generic',
} satisfies Omit<SaveSyntheticDatasetFormValues, 'name'>;

export function SaveSyntheticDatasetDialog({
  open,
  onClose,
  defaultName,
  itemCount,
  onSave,
}: SaveSyntheticDatasetDialogProps) {
  const styles = useStyles();
  const [name, setName] = useState(defaultName ?? '');
  const [nameTouched, setNameTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Keep the input synced with the latest defaultName whenever the dialog
  // re-opens (e.g. after a previous failed save).
  useEffect(() => {
    if (open) {
      setName(defaultName ?? '');
      setNameTouched(false);
      setIsSubmitting(false);
    }
  }, [open, defaultName]);

  const trimmedName = name.trim();
  const isValid = trimmedName.length > 0;
  const nameError = nameTouched && !isValid ? NAME_REQUIRED_ERROR : null;

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const handleSave = useCallback(async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSave({
        name: trimmedName,
        accessCategory: DEFAULTS.accessCategory,
        sensitivityLabel: DEFAULTS.sensitivityLabel,
        dataType: DEFAULTS.dataType,
      });
      onClose();
    } catch {
      // Caller surfaces the error (e.g. via a toast); keep the dialog open.
      setIsSubmitting(false);
    }
  }, [isValid, isSubmitting, onSave, trimmedName, onClose]);

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Save dataset</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Body1 className={styles.itemCountHint}>
                {itemCount.toLocaleString()} item{itemCount === 1 ? '' : 's'}{' '}
                will be uploaded as a Draft dataset. You can publish it from the
                dataset detail page.
              </Body1>

              <Field
                label="Dataset name"
                required
                validationState={nameError ? 'error' : 'none'}
                validationMessage={nameError ?? undefined}
              >
                <Input
                  autoComplete="off"
                  value={name}
                  onChange={(_e, data) => {
                    setName(data.value);
                  }}
                  onBlur={() => {
                    setNameTouched(true);
                  }}
                  disabled={isSubmitting}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              appearance="secondary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              appearance="primary"
              disabled={!isValid || isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void handleSave();
              }}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
